import { FC, useState, useEffect, useRef, type ChangeEvent } from 'react';
import { Tooltip, Form, notification } from 'antd';
import { Button, Search, DataGrid, Modal, Icon, Toggle, Input } from '@hydrodigital/design-system';
import { apiController } from '@/utils/api';
import { useNavigate } from 'react-router-dom';
import LocationService from '@/domain/Location/LocationService';
import Locations from '@/components/Locations/Locations/Locations';
import SelectLocation from '@/basicsComponents/Selects/SelectLocation';
import StateMachineService from '@/domain/StateMachine/StateMachineService';
import ConfirmationModal from '@/components/Modal/ConfirmationModal';
import SelectInpFile from '@/basicsComponents/Selects/SelectInpFile';
import dayjs from 'dayjs';
import useDebounce from '@/hooks/useDebounce';

const locationService = new LocationService();
const stateMachineService = new StateMachineService();

interface DataType {
	id: number;
	name: string;
	inpName: string;
	location: string;
	createdBy: number;
	createdAt: number;
	enabled: boolean;
}

const ControllersTab: FC = () => {
	const userId = localStorage.getItem('userId') || '';
	const security = localStorage.getItem('security');
	const organizationId = JSON.parse(security || '{}').organizationId;
	const [name, setName] = useState<string>('');
	const [locationId, setLocationId] = useState<string | null>(null);
	const [inUse, setInUse] = useState<any>(null);
	const [location, setLocation] = useState<any>(null);
	const [locations, setLocations] = useState<any>(null);
	const [stateMachines, setStateMachines] = useState([]);
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const [selectedStateMachine, setSelectedStateMachine] = useState<any>(null);
	const [paginationState, setPaginationState] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [totalRows, setTotalRows] = useState(0);
	const selectLocationRef = useRef<any>(null);
	const [showNewModal, setShowNewModal] = useState<boolean>(false);
	const [form] = Form.useForm();
	const [selectedLocation, setSelectedLocation] = useState(null);

	const navigate = useNavigate();

	const debouncedName = useDebounce(name, 500);

	useEffect(() => {
		handleGetStateMachines();
		return () => {
			apiController.abort();
		};
	}, [paginationState.pageIndex, paginationState.pageSize, debouncedName, locationId]);

	useEffect(() => {
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, [debouncedName, locationId]);

	useEffect(() => {
		const userId = localStorage.getItem('userId') || '';
		const resLocationByUser = locationService.getLocationsByUser(userId);
		resLocationByUser.then((value) => {
			setLocations(value);
		});
	}, []);

	const handleGetStateMachines = async () => {
		const response = await stateMachineService.getAllStateMachines({
			name: debouncedName || null,
			locationId: locationId || null,
			page: paginationState.pageIndex + 1,
			size: paginationState.pageSize,
		});
		if (response) {
			setTotalRows(response.totalRecords);
			setStateMachines(response.data);
		}
	};

	const handleSaveStateMachine = async (values: {
		name: string | null;
		locationId: string | number | null;
		inpId: string | null;
		enabled: boolean;
	}) => {
		const response = await stateMachineService.createStateMachine(values);
		if (response) {
			setShowNewModal(false);
			notification.success({
				message: 'Successful',
				description: 'Smart Control created successfully',
			});
			handleGetStateMachines();
		}
	};

	const handleChangeEnabled = async (record: DataType, enabled: boolean) => {
		try {
			await stateMachineService.updateStateMachineStatus(record.id, enabled);
			const newData: any = [...stateMachines];
			const index = newData.findIndex((item: any) => record.id === item.id);
			newData[index].enabled = enabled;
			setStateMachines(newData);
		} catch (error) {
			console.error(error);
		}
	};

	const columns = [
		{
			accessorKey: 'name',
			header: 'Controller',
			cell: (cellContext: any) => (
				<span style={{ color: '#232B36', fontSize: '14px', fontWeight: '500' }}>
					{cellContext.getValue()}
				</span>
			),
		},
		{
			accessorKey: 'locationName',
			header: 'Location',
			cell: (cellContext: any) => <span>{cellContext.getValue()}</span>,
		},
		{
			accessorKey: 'inpName',
			header: 'Inp File',
			cell: (cellContext: any) => <span>{cellContext.getValue()}</span>,
		},
		{
			accessorKey: 'createdBy',
			header: 'Added by',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as DataType;
				return (
					<div className="flex column">
						<span
							style={{
								color: '#232B36',
								fontSize: '14px',
								fontWeight: '500',
							}}
						>
							{row.createdBy}
						</span>
						<span
							style={{
								color: '#74788D',
							}}
						>
							{dayjs(row.createdAt).format('YYYY/MM/DD [at] hh:mm a')}
						</span>
					</div>
				);
			},
		},
		{
			accessorKey: 'enabled',
			header: 'Enable',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as DataType;
				return (
					<Tooltip title="Changes in status of smart control will appear in the next simulation">
						<div className="flex">
							<Toggle
								key={`toggle-${row.id}-${row.enabled}`}
								defaultChecked={row.enabled}
								checkedText="ON"
								uncheckedText="OFF"
								size="small"
								onChange={(e: ChangeEvent<HTMLInputElement>) => {
									handleChangeEnabled(row, e.target.checked);
								}}
							/>
						</div>
					</Tooltip>
				);
			},
		},
		{
			accessorKey: 'actions',
			header: 'Actions',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as DataType;
				return (
					<div className="flex" style={{ gap: '12px' }}>
						<Tooltip title="Edit">
							<span
								style={{ cursor: 'pointer', display: 'inline-flex' }}
								onClick={() => {
									navigate(`/stateMachines/edit/${row.id}`);
								}}
							>
								<Icon name="edit-alt" size="m" hoverable />
							</span>
						</Tooltip>
					</div>
				);
			},
		},
	];

	return (
		<div className="flex flex-col" style={{ height: 'calc(100vh - 220px)' }}>
			<div className="flex justify-between" style={{ flexShrink: 0 }}>
				<div className="flex">
					<div style={{ width: 200, minWidth: 200 }}>
						<Search
							placeholder="Search"
							size="small"
							value={name}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								setName(e.target.value);
							}}
							showClearButton={true}
							onClear={() => {
								setName('');
							}}
							style={{ width: '100%' }}
						/>
					</div>
					<div style={{ marginLeft: 30 }}>
						<SelectLocation
							ref={selectLocationRef}
							userId={userId}
							onSelect={(value: any) => {
								setLocation(value);
								setLocationId(value);
							}}
						/>
					</div>
				</div>
				<div>
					<Button
						label="Add Controller"
						size="small"
						variant="secondary"
						iconName="plus-circle"
						onClick={() => {
							setShowNewModal(true);
						}}
					/>
				</div>
			</div>

			<DataGrid
				columns={columns}
				data={stateMachines || []}
				className="mt-3"
				stickyHeader={true}
				maxHeight="calc(100vh - 300px)"
				pagination={{
					enabled: true,
					manualPagination: true,
					rowCount: totalRows,
					defaultPageSize: 10,
					pageSizeOptions: [10, 20, 50],
				}}
				paginationState={paginationState}
				onPaginationChange={setPaginationState}
			/>

			<ConfirmationModal
				title="Delete Control?"
				message="Are you sure to delete it?"
				showModal={showConfirmDelete}
				onAccept={async () => {
					if (selectedStateMachine) {
						// deleteStatemachine(selectedStateMachine?.id);
					}
				}}
				oncancel={() => {
					setShowConfirmDelete(false);
				}}
			/>
			<Modal
				isOpen={showNewModal}
				onClose={() => {
					setShowNewModal(false);
					form.resetFields();
				}}
				size="xs"
				showCloseButton={false}
			>
				<Modal.Header>
					<Modal.Title text="ADD CONTROLLERS" />
				</Modal.Header>

				<Modal.Content>
					<Form
						layout={'vertical'}
						form={form}
						onFinish={(val) => {
							handleSaveStateMachine(val);
						}}
						onFinishFailed={() => {}}
						style={{ maxWidth: 600 }}
					>
						<Form.Item
							label="Name"
							name="name"
							rules={[
								{
									required: true,
								},
							]}
						>
							<Input placeholder="" size="small" />
						</Form.Item>
						<Form.Item
							label="Select location"
							name="locationId"
							rules={[
								{
									required: true,
								},
							]}
						>
							<Locations
								locations={locations}
								handleClickSelect={(val: any) => {
									form.setFieldValue('locationId', val);
									setSelectedLocation(val);
								}}
								defaultValue={false}
							/>
						</Form.Item>
						<Form.Item
							label="Model INP"
							name="inpId"
							rules={[
								{ required: true, message: 'Please select an option' },
								{
									validator: (_, value) =>
										value !== 0
											? Promise.resolve()
											: Promise.reject(new Error('Please select an option')),
								},
							]}
						>
							<SelectInpFile
								onSelect={(val: any) => {
									form.setFieldValue('inpId', val);
								}}
								organizationId={organizationId}
								locationId={form.getFieldValue('locationId')}
								requireLocation
								onlyCompleted
							/>
						</Form.Item>
					</Form>
				</Modal.Content>

				<Modal.Footer className="flex justify-between">
					<Button
						label="Accept"
						variant="primary"
						showIcon={false}
						size="small"
						onClick={() => form.submit()}
					/>
					<Button
						label="Cancel"
						variant="secondary"
						showIcon={false}
						size="small"
						onClick={() => setShowNewModal(false)}
					/>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default ControllersTab;
